import React, { useState, useEffect } from 'react'
import {
  Card, Table, Button, Space, Input, Select, Modal, Form, InputNumber,
  Tag, Popconfirm, Row, Col, Statistic, Alert, App, Descriptions,
  Badge, Typography
} from 'antd'
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SyncOutlined, SettingOutlined,
  SearchOutlined, ReloadOutlined, InfoCircleOutlined, CheckCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons'
import { modelConfigApi } from '@/services/api'
import dayjs from 'dayjs'

const { Text, Title } = Typography

interface ModelConfig {
  id: number
  model_id: string
  model_name: string
  provider: string
  input_price: number
  output_price: number
  currency: string
  item_id?: string
  status: string
  effective_date: number
  created_at: number
  updated_at: number
  // 🔥 新增：model_configs表的字段
  model_type?: string
  tier?: string
  context_window?: number
  max_output_tokens?: number
  supports_vision?: boolean
  supports_audio?: boolean
  supports_code?: boolean
  supports_reasoning?: boolean
  supports_function_calling?: boolean
  supports_streaming?: boolean
  avg_response_time_ms?: number
  tokens_per_second?: number
  reliability_score?: number
  recommended_use_cases?: string
  strengths?: string
  limitations?: string
  default_temperature?: number
  default_top_p?: number
  default_max_tokens?: number
  is_active?: boolean
  is_deprecated?: boolean
  release_date?: string
}

const ModelManagementPage: React.FC = () => {
  const { message } = App.useApp()
  const [models, setModels] = useState<ModelConfig[]>([])
  const [loading, setLoading] = useState(false)
  const [detailModalVisible, setDetailModalVisible] = useState(false)
  const [editModalVisible, setEditModalVisible] = useState(false)
  const [selectedModel, setSelectedModel] = useState<ModelConfig | null>(null)
  const [editingModel, setEditingModel] = useState<ModelConfig | null>(null)
  const [filters, setFilters] = useState({
    status: '',
    provider: '',
    search: ''
  })
  const [providers, setProviders] = useState<string[]>([])
  const [form] = Form.useForm()

  // 获取模型列表
  const fetchModels = async () => {
    setLoading(true)
    try {
      // 🔥 修复：使用带有model_configs关联的API
      const response = await modelConfigApi.getModelConfigsWithDetails(filters)
      if (response.success) {
        console.log('🔍 模型管理数据:', response.data)
        setModels(response.data.models || [])
      } else {
        console.error('❌ 获取模型列表失败:', response.error)
        message.error('获取模型列表失败: ' + response.error)
      }
    } catch (error: any) {
      message.error('获取模型列表失败: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  // 获取提供商列表
  const fetchProviders = async () => {
    try {
      const response = await modelConfigApi.getProviders()
      if (response.success) {
        setProviders(response.data || [])
      }
    } catch (error) {
      console.error('获取提供商列表失败:', error)
    }
  }

  // 同步proxy.js模型配置
  const syncFromProxy = async () => {
    setLoading(true)
    try {
      const response = await modelConfigApi.syncFromProxy()
      if (response.success) {
        const { total, added, updated } = response.data
        message.success(`同步成功，处理了 ${total} 个模型（新增 ${added} 个，更新 ${updated} 个）`)
        fetchModels()
      } else {
        message.error('同步失败: ' + response.error)
      }
    } catch (error: any) {
      message.error('同步失败: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  // 🔥 新增：强制刷新模型列表
  const forceRefreshModels = async () => {
    setLoading(true)
    try {
      // 首先调用proxy.js的强制刷新API
      const refreshResponse = await fetch('http://localhost:8001/models/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || 'refly-zhangxin1212122025'}`
        }
      })

      if (refreshResponse.ok) {
        const refreshData = await refreshResponse.json()
        message.success(`Proxy模型列表已刷新，获取到 ${refreshData.modelCount || 0} 个模型`)

        // 然后同步到后台管理系统
        await syncFromProxy()
      } else {
        message.error('强制刷新失败，请检查proxy.js服务状态')
      }
    } catch (error: any) {
      message.error('强制刷新失败: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  // 查看详情
  const handleViewDetail = (model: ModelConfig) => {
    setSelectedModel(model)
    setDetailModalVisible(true)
  }

  // 编辑模型
  const handleEdit = (model: ModelConfig) => {
    setEditingModel(model)
    form.setFieldsValue({
      model_id: model.model_id,
      model_name: model.model_name,
      provider: model.provider,
      input_price: model.input_price,
      output_price: model.output_price,
      currency: model.currency,
      item_id: model.item_id,
      status: model.status
    })
    setEditModalVisible(true)
  }

  // 保存编辑
  const handleSave = async () => {
    try {
      const values = await form.validateFields()
      
      if (editingModel) {
        // 更新
        const response = await modelConfigApi.updateModel(editingModel.id.toString(), values)
        if (response.success) {
          message.success('更新成功')
          setEditModalVisible(false)
          fetchModels()
        } else {
          message.error('更新失败: ' + response.error)
        }
      } else {
        // 创建
        const response = await modelConfigApi.createModel(values)
        if (response.success) {
          message.success('创建成功')
          setEditModalVisible(false)
          fetchModels()
        } else {
          message.error('创建失败: ' + response.error)
        }
      }
    } catch (error: any) {
      message.error('操作失败: ' + error.message)
    }
  }

  // 删除模型
  const handleDelete = async (id: number) => {
    try {
      const response = await modelConfigApi.deleteModel(id.toString())
      if (response.success) {
        message.success('删除成功')
        fetchModels()
      } else {
        message.error('删除失败: ' + response.error)
      }
    } catch (error: any) {
      message.error('删除失败: ' + error.message)
    }
  }

  // 切换状态
  const handleToggleStatus = async (model: ModelConfig) => {
    const newStatus = model.status === 'active' ? 'inactive' : 'active'
    try {
      const response = await modelConfigApi.updateModel(model.id.toString(), { status: newStatus })
      if (response.success) {
        message.success(`已${newStatus === 'active' ? '启用' : '禁用'}模型`)
        fetchModels()
      } else {
        message.error('状态更新失败: ' + response.error)
      }
    } catch (error: any) {
      message.error('状态更新失败: ' + error.message)
    }
  }

  useEffect(() => {
    fetchModels()
    fetchProviders()
  }, [])

  useEffect(() => {
    fetchModels()
  }, [filters])

  const columns = [
    {
      title: '模型信息',
      key: 'model_info',
      width: 250,
      render: (_: any, record: ModelConfig) => (
        <div>
          <div style={{ fontWeight: 500, marginBottom: 4 }}>
            {record.model_name}
          </div>
          <div style={{ fontSize: '12px', color: '#666', fontFamily: 'monospace' }}>
            {record.model_id.length > 35 ? `${record.model_id.substring(0, 35)}...` : record.model_id}
          </div>
          <div style={{ marginTop: 4 }}>
            <Tag color="blue">{record.provider}</Tag>
            {record.item_id && (
              <Tag color="green">已配置</Tag>
            )}
          </div>
        </div>
      )
    },
    {
      title: '价格信息',
      key: 'pricing',
      width: 200,
      render: (_: any, record: ModelConfig) => (
        <div>
          <div style={{ fontSize: '12px', marginBottom: 2 }}>
            <Text type="secondary">输入: </Text>
            <Text code>${(Number(record.input_price) || 0).toFixed(6)}/1M</Text>
          </div>
          <div style={{ fontSize: '12px' }}>
            <Text type="secondary">输出: </Text>
            <Text code>${(Number(record.output_price) || 0).toFixed(6)}/1M</Text>
          </div>
        </div>
      )
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string, record: ModelConfig) => (
        <div>
          <Badge
            status={status === 'active' ? 'success' : 'error'}
            text={status === 'active' ? '启用' : '禁用'}
          />
          <div style={{ marginTop: 4 }}>
            <Button
              type="link"
              size="small"
              onClick={() => handleToggleStatus(record)}
              style={{ padding: 0, height: 'auto' }}
            >
              {status === 'active' ? '禁用' : '启用'}
            </Button>
          </div>
        </div>
      )
    },
    {
      title: '更新时间',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 120,
      render: (timestamp: number) => (
        <div>
          <div>{dayjs(timestamp).format('MM-DD')}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {dayjs(timestamp).format('HH:mm')}
          </div>
        </div>
      )
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_: any, record: ModelConfig) => (
        <Space size="small" direction="vertical">
          <Space size="small">
            <Button
              type="link"
              size="small"
              icon={<InfoCircleOutlined />}
              onClick={() => handleViewDetail(record)}
            >
              详情
            </Button>
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            >
              编辑
            </Button>
          </Space>
          <Popconfirm
            title="确定删除这个模型配置吗？"
            description="删除后将无法恢复，且可能影响相关的计费计算。"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  // 统计数据
  const totalModels = models.length
  const activeModels = models.filter(m => m.status === 'active').length
  const inactiveModels = models.filter(m => m.status === 'inactive').length
  const configuredModels = models.filter(m => m.item_id).length

  return (
    <div style={{ padding: '24px' }}>
      {/* 统计概览 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="总模型数"
              value={totalModels}
              prefix={<SettingOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="启用模型"
              value={activeModels}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="禁用模型"
              value={inactiveModels}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="已配置模型"
              value={configuredModels}
              suffix={`/ ${totalModels}`}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title="模型管理"
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={forceRefreshModels}
              loading={loading}
              type="default"
            >
              强制刷新模型
            </Button>
            <Button
              icon={<SyncOutlined />}
              onClick={syncFromProxy}
              loading={loading}
            >
              同步Proxy配置
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingModel(null)
                form.resetFields()
                setEditModalVisible(true)
              }}
            >
              添加模型
            </Button>
          </Space>
        }
      >
        {/* 筛选器 */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Input
              placeholder="搜索模型名称或ID"
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              allowClear
            />
          </Col>
          <Col span={4}>
            <Select
              placeholder="选择提供商"
              value={filters.provider}
              onChange={(value) => setFilters({ ...filters, provider: value })}
              allowClear
              style={{ width: '100%' }}
            >
              {providers.map(provider => (
                <Select.Option key={provider} value={provider}>
                  {provider}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="选择状态"
              value={filters.status}
              onChange={(value) => setFilters({ ...filters, status: value })}
              allowClear
              style={{ width: '100%' }}
            >
              <Select.Option value="active">启用</Select.Option>
              <Select.Option value="inactive">禁用</Select.Option>
            </Select>
          </Col>
          <Col span={4}>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchModels}
              loading={loading}
            >
              刷新
            </Button>
          </Col>
        </Row>

        {/* 提示信息 */}
        <Alert
          message="模型管理说明"
          description="在这里可以管理所有AI模型的配置信息。建议定期从proxy.js同步最新的模型列表，确保配置与实际使用保持一致。"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        {/* 表格 */}
        <Table
          columns={columns}
          dataSource={models}
          rowKey="id"
          loading={loading}
          pagination={{
            total: models.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`
          }}
          scroll={{ x: 800 }}
        />
      </Card>

      {/* 详情模态框 */}
      <Modal
        title="模型详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>,
          <Button
            key="edit"
            type="primary"
            onClick={() => {
              setDetailModalVisible(false)
              if (selectedModel) {
                handleEdit(selectedModel)
              }
            }}
          >
            编辑
          </Button>
        ]}
        width={700}
      >
        {selectedModel && (
          <div>
            {/* 基本信息 */}
            <Title level={5}>基本信息</Title>
            <Descriptions column={2} bordered size="small" style={{ marginBottom: 16 }}>
              <Descriptions.Item label="模型ID" span={2}>
                <Text code>{selectedModel.model_id}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="模型名称">
                {selectedModel.model_name}
              </Descriptions.Item>
              <Descriptions.Item label="提供商">
                <Tag color="blue">{selectedModel.provider}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="模型类型">
                {selectedModel.model_type ? (
                  <Tag color="green">{selectedModel.model_type}</Tag>
                ) : (
                  <Text type="secondary">未设置</Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="等级">
                {selectedModel.tier ? (
                  <Tag color="purple">{selectedModel.tier}</Tag>
                ) : (
                  <Text type="secondary">未设置</Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="状态">
                <Badge
                  status={selectedModel.status === 'active' ? 'success' : 'error'}
                  text={selectedModel.status === 'active' ? '启用' : '禁用'}
                />
              </Descriptions.Item>
              <Descriptions.Item label="是否弃用">
                {selectedModel.is_deprecated ? (
                  <Tag color="red">已弃用</Tag>
                ) : (
                  <Tag color="green">正常</Tag>
                )}
              </Descriptions.Item>
            </Descriptions>

            {/* 价格信息 */}
            <Title level={5}>价格信息</Title>
            <Descriptions column={2} bordered size="small" style={{ marginBottom: 16 }}>
              <Descriptions.Item label="输入价格">
                <Text code>${(Number(selectedModel.input_price) || 0).toFixed(6)}/1M tokens</Text>
              </Descriptions.Item>
              <Descriptions.Item label="输出价格">
                <Text code>${(Number(selectedModel.output_price) || 0).toFixed(6)}/1M tokens</Text>
              </Descriptions.Item>
              <Descriptions.Item label="货币">
                {selectedModel.currency}
              </Descriptions.Item>
              <Descriptions.Item label="Item ID">
                {selectedModel.item_id ? (
                  <Text code>{selectedModel.item_id}</Text>
                ) : (
                  <Text type="secondary">未配置</Text>
                )}
              </Descriptions.Item>
            </Descriptions>

            {/* 技术参数 */}
            <Title level={5}>技术参数</Title>
            <Descriptions column={2} bordered size="small" style={{ marginBottom: 16 }}>
              <Descriptions.Item label="上下文窗口">
                {selectedModel.context_window ? (
                  <Text>{(selectedModel.context_window / 1000).toFixed(0)}K tokens</Text>
                ) : (
                  <Text type="secondary">未设置</Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="最大输出">
                {selectedModel.max_output_tokens ? (
                  <Text>{(selectedModel.max_output_tokens / 1000).toFixed(0)}K tokens</Text>
                ) : (
                  <Text type="secondary">未设置</Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="平均响应时间">
                {selectedModel.avg_response_time_ms ? (
                  <Text>{selectedModel.avg_response_time_ms}ms</Text>
                ) : (
                  <Text type="secondary">未设置</Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="处理速度">
                {selectedModel.tokens_per_second ? (
                  <Text>{selectedModel.tokens_per_second} tokens/s</Text>
                ) : (
                  <Text type="secondary">未设置</Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="可靠性评分">
                {selectedModel.reliability_score ? (
                  <Text>{(selectedModel.reliability_score * 100).toFixed(1)}%</Text>
                ) : (
                  <Text type="secondary">未设置</Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="发布日期">
                {selectedModel.release_date ? (
                  <Text>{selectedModel.release_date}</Text>
                ) : (
                  <Text type="secondary">未设置</Text>
                )}
              </Descriptions.Item>
            </Descriptions>

            {/* 能力支持 */}
            <Title level={5}>能力支持</Title>
            <div style={{ marginBottom: 16 }}>
              <Space wrap>
                {selectedModel.supports_vision && <Tag color="orange">视觉支持</Tag>}
                {selectedModel.supports_audio && <Tag color="cyan">音频支持</Tag>}
                {selectedModel.supports_code && <Tag color="geekblue">代码支持</Tag>}
                {selectedModel.supports_reasoning && <Tag color="red">推理支持</Tag>}
                {selectedModel.supports_function_calling && <Tag color="green">函数调用</Tag>}
                {selectedModel.supports_streaming && <Tag color="blue">流式输出</Tag>}
                {!selectedModel.supports_vision && !selectedModel.supports_audio &&
                 !selectedModel.supports_code && !selectedModel.supports_reasoning &&
                 !selectedModel.supports_function_calling && !selectedModel.supports_streaming && (
                  <Text type="secondary">暂无能力信息</Text>
                )}
              </Space>
            </div>

            {/* 默认参数 */}
            {(selectedModel.default_temperature || selectedModel.default_top_p || selectedModel.default_max_tokens) && (
              <>
                <Title level={5}>默认参数</Title>
                <Descriptions column={2} bordered size="small" style={{ marginBottom: 16 }}>
                  {selectedModel.default_temperature && (
                    <Descriptions.Item label="默认温度">
                      <Text>{selectedModel.default_temperature}</Text>
                    </Descriptions.Item>
                  )}
                  {selectedModel.default_top_p && (
                    <Descriptions.Item label="默认Top-P">
                      <Text>{selectedModel.default_top_p}</Text>
                    </Descriptions.Item>
                  )}
                  {selectedModel.default_max_tokens && (
                    <Descriptions.Item label="默认最大Token">
                      <Text>{selectedModel.default_max_tokens}</Text>
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </>
            )}

            {/* 使用建议 */}
            {(selectedModel.recommended_use_cases || selectedModel.strengths || selectedModel.limitations) && (
              <>
                <Title level={5}>使用建议</Title>
                <Descriptions column={1} bordered size="small" style={{ marginBottom: 16 }}>
                  {selectedModel.recommended_use_cases && (
                    <Descriptions.Item label="推荐用途">
                      <Text>{selectedModel.recommended_use_cases}</Text>
                    </Descriptions.Item>
                  )}
                  {selectedModel.strengths && (
                    <Descriptions.Item label="优势">
                      <Text>{selectedModel.strengths}</Text>
                    </Descriptions.Item>
                  )}
                  {selectedModel.limitations && (
                    <Descriptions.Item label="限制">
                      <Text>{selectedModel.limitations}</Text>
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </>
            )}

            {/* 时间信息 */}
            <Title level={5}>时间信息</Title>
            <Descriptions column={2} bordered size="small">
              <Descriptions.Item label="创建时间">
                {dayjs(selectedModel.created_at).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
              <Descriptions.Item label="更新时间">
                {dayjs(selectedModel.updated_at).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
              <Descriptions.Item label="生效时间">
                {dayjs(selectedModel.effective_date).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Modal>

      {/* 编辑模态框 */}
      <Modal
        title={editingModel ? '编辑模型配置' : '添加模型配置'}
        open={editModalVisible}
        onOk={handleSave}
        onCancel={() => setEditModalVisible(false)}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            currency: 'USD',
            status: 'active'
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="模型ID"
                name="model_id"
                rules={[{ required: true, message: '请输入模型ID' }]}
              >
                <Input placeholder="例如: openai/gpt-4" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="模型名称"
                name="model_name"
                rules={[{ required: true, message: '请输入模型名称' }]}
              >
                <Input placeholder="例如: GPT-4" />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="提供商"
                name="provider"
                rules={[{ required: true, message: '请输入提供商' }]}
              >
                <Input placeholder="例如: OpenAI" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="状态"
                name="status"
                rules={[{ required: true, message: '请选择状态' }]}
              >
                <Select>
                  <Select.Option value="active">启用</Select.Option>
                  <Select.Option value="inactive">禁用</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="输入价格 ($/1M tokens)"
                name="input_price"
                rules={[{ required: true, message: '请输入输入价格' }]}
              >
                <InputNumber
                  min={0}
                  step={0.000001}
                  precision={6}
                  style={{ width: '100%' }}
                  placeholder="0.000000"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="输出价格 ($/1M tokens)"
                name="output_price"
                rules={[{ required: true, message: '请输入输出价格' }]}
              >
                <InputNumber
                  min={0}
                  step={0.000001}
                  precision={6}
                  style={{ width: '100%' }}
                  placeholder="0.000000"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="货币"
                name="currency"
              >
                <Select>
                  <Select.Option value="USD">USD</Select.Option>
                  <Select.Option value="CNY">CNY</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Item ID"
                name="item_id"
              >
                <Input placeholder="可选，用于与proxy.js同步" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  )
}

export default ModelManagementPage
